// Types
import type { KeyedMutator, SWRConfiguration } from 'swr';
import useSWR from 'swr';

// Hooks
import useAcl from '@/hooks/user/useAcl';

import { get } from '@/utils/client';

import type { EmailType } from '@/types/brandedTypes/email';

// Libs
import { aclFeatures } from '@/lib/acl/aclFeatures';
import { PERMISSIONS } from '@/lib/permissions/socialPermissions';

import { SETTINGS_DATA_ACCESS } from '@/routes/apiEndpoints';

export type PermissionType =
    | typeof PERMISSIONS.ADS
    | typeof PERMISSIONS.ANALYTICS
    | typeof PERMISSIONS.SCHEDULER
    | typeof PERMISSIONS.INSTAGRAM_DM
    | typeof PERMISSIONS.MESSENGER_DM
    | typeof PERMISSIONS.SCHEDULER_MENTIONS;

export type UserFBPermissionsType = {
    not_applicable: boolean;
    permissions: PermissionType[];
    user: {
        id: string;
        name: string;
        email: EmailType;
        picture: {
            data: {
                url: string;
                width: number;
                height: number;
                is_silhouette: boolean;
            };
        };
        short_name: string;
    };
    refresh_date: string;
};

export type MutateUserFBPermissions = KeyedMutator<UserFBPermissionsType>;

type UseUserFBPermissionsProps = {
    params?: { update?: '1' };
    options?: SWRConfiguration<UserFBPermissionsType, Error>;
};

const useUserFBPermissions = ({ params = {}, options = {} }: UseUserFBPermissionsProps = {}) => {
    const { hasAcl } = useAcl();

    const { data, mutate, error, isLoading } = useSWR<UserFBPermissionsType, Error>(
        hasAcl(aclFeatures.account.data_access.read)
            ? [SETTINGS_DATA_ACCESS, JSON.stringify(params)]
            : null,
        ([url]) => get(url, params).then((res) => res.data),
        {
            shouldRetryOnError: false,
            ...options
        }
    );

    return {
        user: data,
        mutate,
        error,
        isLoading
    };
};
export default useUserFBPermissions;
